import React, { useMemo, useState } from 'react';
import { Search, Eye, AlertCircle, CheckCircle, Layout, X, ChevronLeft } from 'lucide-react';
import { Question, ModuleId } from '../../../types';
import { MODULES } from '../../../data/modules';
import { TIERS } from '../../../config/tiers';
import { Badge } from '../../ui/Badge';
import { QuestionCardClean } from '../../practice/QuestionCardClean';
import { QuestionRendererFactory } from '../../practice/renderers/QuestionRendererFactory';
import { getFamilyDefinition } from '../../../config/taxonomyRegistry';
interface QuestionExplorerProps {
  questions: Question[];
}
export function QuestionExplorer({
  questions
}: QuestionExplorerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<ModuleId | 'all'>('all');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [previewQuestion, setPreviewQuestion] = useState<Question | null>(null);
  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      const matchesSearch = q.question.toLowerCase().includes(searchTerm.toLowerCase()) || q.id.toLowerCase().includes(searchTerm.toLowerCase()) || q.familyId && q.familyId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesModule = selectedModule === 'all' || q.module === selectedModule;
      const matchesTier = selectedTier === 'all' || q.tier != null && q.tier.toString() === selectedTier;
      return matchesSearch && matchesModule && matchesTier;
    });
  }, [questions, searchTerm, selectedModule, selectedTier]);
  // Get family definition for preview
  const previewInfo = useMemo(() => {
    if (!previewQuestion || !previewQuestion.familyId) return null;
    const familyDef = getFamilyDefinition(previewQuestion.familyId);
    return {
      familyDef
    };
  }, [previewQuestion]);
  return <div className="flex flex-col h-full" data-id="element-1832">
      {/* Compact Filters */}
      <div className="flex-none p-4 border-b border-slate-200 bg-white space-y-3" data-id="element-1833">
        <div className="flex gap-3 flex-wrap" data-id="element-1834">
          <div className="relative flex-1 min-w-[200px]" data-id="element-1835">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1836" />
            <input type="text" placeholder="Zoek op vraag, ID of family..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1837" />
          </div>
          <select value={selectedModule} onChange={e => setSelectedModule(e.target.value as ModuleId | 'all')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1838">
            <option value="all" data-id="element-1839">Alle Vakken</option>
            {MODULES.map(m => <option key={m.id} value={m.id} data-id="element-1840">
                {m.title}
              </option>)}
          </select>
          <select value={selectedTier} onChange={e => setSelectedTier(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1841">
            <option value="all" data-id="element-1842">Alle Tiers</option>
            {TIERS.map(t => <option key={t.tier} value={t.tier.toString()} data-id="element-1843">
                {t.levelCode} (Tier {t.tier})
              </option>)}
          </select>
        </div>
        <div className="flex items-center justify-between text-xs text-slate-400" data-id="element-1844">
          <span data-id="element-1845">{filteredQuestions.length} vragen gevonden</span>
          {previewQuestion && <button onClick={() => setPreviewQuestion(null)} className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1" data-id="element-1846">
              <X className="w-3 h-3" data-id="element-1847" /> Sluit preview
            </button>}
        </div>
      </div>

      {/* Content: List + Preview */}
      <div className="flex-1 min-h-0 flex" data-id="element-1848">
        {/* Question List */}
        <div className={`
          ${previewQuestion ? 'hidden lg:flex' : 'flex'}
          flex-col w-full lg:w-[340px] lg:flex-shrink-0 border-r border-slate-100 bg-white
        `} data-id="element-1849">
          <div className="flex-1 overflow-y-auto" data-id="element-1850">
            {filteredQuestions.slice(0, 100).map(q => <button key={q.id} onClick={() => setPreviewQuestion(q)} className={`
                  w-full text-left px-4 py-3 border-b border-slate-100 transition-colors
                  ${previewQuestion?.id === q.id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-slate-50 border-l-2 border-l-transparent'}
                `} data-id="element-1851">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap" data-id="element-1852">
                  <span className="font-mono text-[10px] text-slate-400 bg-slate-100 px-1 py-0.5 rounded" data-id="element-1853">
                    {q.id}
                  </span>
                  <Badge variant="outline" size="sm" className="text-[10px]" data-id="element-1854">
                    {q.module}
                  </Badge>
                  <Badge variant="secondary" size="sm" className="text-[10px]" data-id="element-1855">
                    Tier {q.tier}
                  </Badge>
                </div>
                <p className="text-sm text-slate-800 line-clamp-2 leading-snug" data-id="element-1856">
                  {q.question}
                </p>
              </button>)}
            {filteredQuestions.length > 100 && <div className="text-center py-4 text-slate-400 text-xs" data-id="element-1857">
                +{filteredQuestions.length - 100} meer (gebruik filters)
              </div>}
            {filteredQuestions.length === 0 && <div className="text-center py-12 text-slate-400 text-sm" data-id="element-1858">
                Geen vragen gevonden
              </div>}
          </div>
        </div>

        {/* Preview Pane */}
        <div className={`
          ${previewQuestion ? 'flex' : 'hidden lg:flex'}
          flex-col flex-1 min-w-0 bg-slate-50
        `} data-id="element-1859">
          {previewQuestion ? <div className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-1860">
              {/* Back button on mobile/tablet */}
              <button onClick={() => setPreviewQuestion(null)} className="lg:hidden flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-id="element-1861">
                <ChevronLeft className="w-4 h-4" data-id="element-1862" /> Terug naar lijst
              </button>

              <div className="max-w-2xl mx-auto space-y-5" data-id="element-1863">
                {/* Preview header */}
                <div className="flex items-center justify-between" data-id="element-1864">
                  <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2" data-id="element-1865">
                    <Layout className="w-4 h-4" data-id="element-1866" />
                    Live Preview
                  </h3>
                  <div className="flex gap-2" data-id="element-1867">
                    {previewQuestion.feedback?.explanationText ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100" data-id="element-1868">
                        <CheckCircle className="w-3 h-3" data-id="element-1869" /> Uitleg
                      </span> : <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100" data-id="element-1870">
                        <AlertCircle className="w-3 h-3" data-id="element-1871" /> Geen uitleg
                      </span>}
                  </div>
                </div>

                {/* Rendered Question */}
                <QuestionCardClean question={previewQuestion} data-id="element-1872">
                  <QuestionRendererFactory question={previewQuestion} onAnswer={() => {}} data-id="element-1873" />
                </QuestionCardClean>

                {/* Debug Info */}
                <div className="bg-slate-800 text-slate-200 rounded-xl p-4" data-id="element-1874">
                  <h4 className="font-mono text-[10px] font-bold text-slate-400 uppercase mb-3 border-b border-slate-700 pb-2" data-id="element-1875">
                    Debug Data
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-xs font-mono" data-id="element-1876">
                    <div data-id="element-1877">
                      <span className="text-slate-500 block text-[10px]" data-id="element-1878">
                        Type
                      </span>
                      <span className="text-indigo-400" data-id="element-1879">
                        {previewQuestion.questionType || 'multiple_choice'}
                      </span>
                    </div>
                    <div data-id="element-1880">
                      <span className="text-slate-500 block text-[10px]" data-id="element-1881">
                        Family ID
                      </span>
                      <span className="text-emerald-400" data-id="element-1882">
                        {previewQuestion.familyId || '-'}
                      </span>
                    </div>

                    {/* Taxonomy Info */}
                    {previewInfo?.familyDef && <>
                        <div data-id="element-1883">
                          <span className="text-slate-500 block text-[10px]" data-id="element-1884">
                            Tier / Level
                          </span>
                          <span className="text-slate-300" data-id="element-1885">
                            Tier {previewInfo.familyDef.tier} (
                            {previewInfo.familyDef.levelCode})
                          </span>
                        </div>

                        <div className="col-span-2" data-id="element-1886">
                          <span className="text-slate-500 block text-[10px]" data-id="element-1887">
                            Description
                          </span>
                          <span className="text-slate-300 italic" data-id="element-1888">
                            {previewInfo.familyDef.description}
                          </span>
                        </div>
                      </>}

                    <div className="col-span-2 pt-2 border-t border-slate-700 mt-1" data-id="element-1889">
                      <span className="text-slate-500 block text-[10px] mb-1" data-id="element-1890">
                        Skill / SubSkill
                      </span>
                    </div>
                    <div data-id="element-1891">
                      <span className="text-slate-500 block text-[10px]" data-id="element-1892">
                        Skill
                      </span>
                      <span className="text-amber-400" data-id="element-1893">
                        {previewQuestion.skill || '-'}
                      </span>
                    </div>
                    <div data-id="element-1894">
                      <span className="text-slate-500 block text-[10px]" data-id="element-1895">
                        SubSkill
                      </span>
                      <span className="text-amber-400" data-id="element-1896">
                        {previewQuestion.subSkill || '-'}
                      </span>
                    </div>
                  </div>

                  {previewQuestion.interactiveConfig && <div className="mt-3 pt-3 border-t border-slate-700" data-id="element-1897">
                      <span className="text-slate-500 block text-[10px] font-mono mb-1" data-id="element-1898">
                        Interactive Config
                      </span>
                      <pre className="bg-slate-900 p-2 rounded text-[10px] overflow-x-auto text-slate-300" data-id="element-1899">
                        {JSON.stringify(previewQuestion.interactiveConfig, null, 2)}
                      </pre>
                    </div>}
                </div>

                {/* Explanation */}
                {previewQuestion.feedback?.explanationText && <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4" data-id="element-1900">
                    <h4 className="font-bold text-emerald-800 text-sm mb-1" data-id="element-1901">
                      Uitleg
                    </h4>
                    <p className="text-emerald-900 text-sm" data-id="element-1902">
                      {previewQuestion.feedback.explanationText}
                    </p>
                  </div>}
              </div>
            </div> : <div className="flex-1 flex items-center justify-center text-slate-400" data-id="element-1903">
              <div className="text-center" data-id="element-1904">
                <Eye className="w-10 h-10 mx-auto mb-3 opacity-30" data-id="element-1905" />
                <p className="text-sm" data-id="element-1906">
                  Selecteer een vraag om een preview te zien
                </p>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}